const FACET_ORDER = ['stage', 'outcome', 'bucket', 'humanDecision', 'status', 'type', 'priority', 'category', 'context', 'dimension', 'workType', 'reviewState', 'approvalStatus', 'organization'];
const SKIP_FACET = new Set(['title', 'reference', 'remarks', 'outcomeRemarks', 'portalReference', 'activity', 'personName', 'personnelName', 'designation', 'recommendation']);
const DATE_KEY = /deadline|date|validto|submittedat|periodstart|duedate/i;
const VALUE_KEYS = new Set(['estimatedValue', 'value', 'openValue', 'awardedValue', 'totalCostPaise', 'indicativePricePaise', 'effortCostPaise']);
const CRORE = 10000000;

export function cellText(column, row) {
  if (column.render) {
    const rendered = column.render(row);
    if (rendered == null || typeof rendered === 'object') return '';
    return String(rendered);
  }
  const raw = row[column.key];
  return raw == null ? '' : String(raw);
}

function uniqueOptions(rows, column) {
  const map = new Map();
  rows.forEach((row) => {
    const raw = row[column.key];
    if (raw == null || raw === '' || typeof raw === 'object') return;
    const value = String(raw);
    const label = cellText(column, row) || value;
    if (!map.has(value)) map.set(value, { value, label, count: 0 });
    map.get(value).count += 1;
  });
  return [...map.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

function croreStep(maxRupees) {
  const steps = [1, 5, 10, 25, 50, 100, 250];
  const found = steps.find((step) => maxRupees / (step * CRORE) <= 6);
  return (found || 250) * CRORE;
}

function formatCrore(rupees) {
  const crore = rupees / CRORE;
  return Number.isInteger(crore) ? String(crore) : crore.toFixed(1);
}

export function valueBands(rows, key) {
  const divisor = /paise/i.test(key) ? 100 : 1;
  const amounts = rows
    .map((row) => Number(row[key]))
    .filter((amount) => Number.isFinite(amount) && amount > 0)
    .map((amount) => amount / divisor);
  if (amounts.length < 2) return [];
  const currencies = new Set(rows.map((row) => row.currency || 'INR'));
  if (currencies.size > 1) return [];
  const step = croreStep(Math.max(...amounts));
  const top = Math.ceil(Math.max(...amounts) / step) * step;
  const bands = [];
  for (let edge = step; edge < top; edge += step) {
    const min = edge - step;
    bands.push({
      id: `${min}-${edge}`,
      label: min === 0 ? `Under ₹${formatCrore(edge)} Cr` : `₹${formatCrore(min)}–${formatCrore(edge)} Cr`,
      min,
      max: edge
    });
  }
  bands.push({
    id: `${top - step}+`,
    label: `₹${formatCrore(top - step)} Cr and over`,
    min: top - step,
    max: Infinity
  });
  return bands.filter((band) => amounts.some((amount) => amount >= band.min && amount < band.max));
}

const EXTRA_LABELS = {
  sector: 'Sector',
  serviceType: 'Service',
  organization: 'Client',
  stage: 'Stage',
  status: 'Status'
};

function pretty(value) {
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function describeReport(columns, rows, extraKeys = [], barKey = '') {
  const known = new Set(columns.map((column) => column.key));
  const extras = extraKeys
    .filter((key) => !known.has(key) && rows.some((row) => row[key]))
    .map((key) => ({
      key,
      label: EXTRA_LABELS[key] || pretty(key),
      render: key === 'organization' ? undefined : (row) => pretty(row[key])
    }));
  const allColumns = [...columns, ...extras];
  const facets = [];
  const ranked = [...allColumns].sort((a, b) => {
    const ai = FACET_ORDER.indexOf(a.key);
    const bi = FACET_ORDER.indexOf(b.key);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
  ranked.forEach((column) => {
    if (SKIP_FACET.has(column.key) || VALUE_KEYS.has(column.key) || DATE_KEY.test(column.key)) return;
    const options = uniqueOptions(rows, column);
    if (options.length < 2 || options.length > 12) return;
    facets.push({ key: column.key, label: column.label, options });
  });
  if (barKey) {
    facets.sort((a, b) => (a.key === barKey ? -1 : b.key === barKey ? 1 : 0));
  }
  const dateColumn = columns.find((column) => DATE_KEY.test(column.key) && rows.some((row) => row[column.key]));
  const valueColumn = columns.find((column) => VALUE_KEYS.has(column.key) && rows.some((row) => Number(row[column.key]) > 0));
  return {
    columns: allColumns,
    bar: barKey,
    facets,
    date: dateColumn ? { key: dateColumn.key, label: dateColumn.label } : null,
    value: valueColumn ? {
      key: valueColumn.key,
      label: valueColumn.label,
      divisor: /paise/i.test(valueColumn.key) ? 100 : 1,
      bands: valueBands(rows, valueColumn.key)
    } : null
  };
}

function startOfDay(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
}

function matchesDate(value, criteria) {
  const { datePreset, dateFrom, dateTo } = criteria;
  if ((!datePreset || datePreset === 'any') && !dateFrom && !dateTo) return true;
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return false;
  const today = startOfDay(new Date());
  if (datePreset === 'overdue') return date < today;
  if (datePreset === 'next30' || datePreset === 'next90') {
    const end = new Date(today);
    end.setDate(end.getDate() + (datePreset === 'next30' ? 30 : 90));
    end.setHours(23, 59, 59, 999);
    return date >= today && date <= end;
  }
  if (dateFrom) {
    const from = startOfDay(dateFrom);
    if (from && date < from) return false;
  }
  if (dateTo) {
    const to = startOfDay(dateTo);
    if (to) {
      to.setHours(23, 59, 59, 999);
      if (date > to) return false;
    }
  }
  return true;
}

export function rowMatches(row, columns, spec, criteria, ignoreFacet) {
  const query = String(criteria.query || '').trim().toLowerCase();
  if (query) {
    const haystack = columns.map((column) => cellText(column, row)).join(' ').toLowerCase();
    if (!haystack.includes(query)) return false;
  }
  const picked = criteria.picked || {};
  const facetMiss = Object.entries(picked).some(([key, values]) => {
    if (key === ignoreFacet || !values?.length) return false;
    return !values.includes(String(row[key] ?? ''));
  });
  if (facetMiss) return false;
  if (spec.date && !matchesDate(row[spec.date.key], criteria)) return false;
  if (spec.value && criteria.band && criteria.band !== 'any') {
    const band = spec.value.bands.find((item) => item.id === criteria.band);
    const amount = Number(row[spec.value.key] || 0) / spec.value.divisor;
    if (!band || amount < band.min || amount >= band.max) return false;
  }
  return true;
}

export function facetCounts(rows, columns, spec, criteria, facet) {
  const counts = Object.fromEntries(facet.options.map((option) => [option.value, 0]));
  rows.forEach((row) => {
    if (!rowMatches(row, columns, spec, criteria, facet.key)) return;
    const value = String(row[facet.key] ?? '');
    if (counts[value] != null) counts[value] += 1;
  });
  return counts;
}

function compareValues(left, right) {
  const leftNumber = Number(left);
  const rightNumber = Number(right);
  const leftDate = left ? new Date(left).getTime() : NaN;
  const rightDate = right ? new Date(right).getTime() : NaN;
  if (typeof left !== 'object' && typeof right !== 'object' && Number.isFinite(leftNumber) && Number.isFinite(rightNumber) && String(left).trim() !== '' && String(right).trim() !== '') {
    return leftNumber - rightNumber;
  }
  if (Number.isFinite(leftDate) && Number.isFinite(rightDate) && /[/-]|\d{4}/.test(String(left)) && /[/-]|\d{4}/.test(String(right))) {
    return leftDate - rightDate;
  }
  return String(left ?? '').localeCompare(String(right ?? ''), undefined, { numeric: true, sensitivity: 'base' });
}

export function sortRows(rows, sort) {
  if (!sort?.key) return rows;
  const direction = sort.dir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => direction * compareValues(a[sort.key], b[sort.key]));
}
